import React, { useEffect, useState } from "react";
import api from "../api";
import { ShieldCheck, AlertTriangle, RefreshCw, ExternalLink } from "lucide-react";

const MAXVALLE_LOGO = "/maxvalle-logo.svg";

/**
 * /privacy — Server-side proxied Privacy Policy.
 *
 * Why this exists: emails sent via Resend land in more inboxes when the
 * privacy-policy link they contain points to the same domain as the
 * sender (sender-domain alignment / SPF-DKIM-DMARC + brand-safety
 * scoring). The actual policy is hosted on privacylab.it; the backend
 * fetches and sanitizes it server-side so we can render it natively
 * under our own domain.
 */
export default function Privacy() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api
      .get("/privacy/content")
      .then((r) => {
        if (!mounted) return;
        setData(r.data);
        setLoading(false);
      })
      .catch((e) => {
        if (!mounted) return;
        setError(e?.response?.data?.detail || "Impossibile caricare l'informativa.");
        setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="privacy-shell" data-testid="privacy-page">
      <header className="privacy-header">
        <a
          href="https://maxvalle.it"
          target="_blank"
          rel="noreferrer"
          className="privacy-brand"
          data-testid="privacy-brand"
        >
          <img src={MAXVALLE_LOGO} alt="Max Valle" />
        </a>
        <a
          className="privacy-back-link"
          href="https://maxvalle.it"
          target="_blank"
          rel="noreferrer"
          data-testid="privacy-back-link"
        >
          maxvalle.it <ExternalLink size={14} />
        </a>
      </header>

      <main className="privacy-main">
        <div className="privacy-card">
          <div className="privacy-title-row">
            <ShieldCheck size={28} className="privacy-shield" />
            <h1 data-testid="privacy-title">Informativa sulla Privacy</h1>
          </div>
          <p className="privacy-lede">
            Documento ufficiale sul trattamento dei dati personali &mdash; versione
            sincronizzata dal nostro <em>privacy manager</em>.
          </p>

          {loading && (
            <div className="privacy-loading" data-testid="privacy-loading">
              <RefreshCw size={20} className="spin" />
              <span>Caricamento dell&apos;informativa&hellip;</span>
            </div>
          )}

          {error && (
            <div className="privacy-error" data-testid="privacy-error">
              <AlertTriangle size={28} />
              <div>
                <strong>Impossibile mostrare l&apos;informativa.</strong>
                <p>{error}</p>
                <a
                  className="privacy-fallback-link"
                  href="https://www.privacylab.it/archive.php?id=3042797&idDoc=51&idTarget=444217&output=html"
                  target="_blank"
                  rel="noreferrer"
                >
                  Apri l&apos;informativa originale &uarr;
                </a>
              </div>
            </div>
          )}

          {data?.html && (
            <article
              className="privacy-content"
              data-testid="privacy-content"
              dangerouslySetInnerHTML={{ __html: data.html }}
            />
          )}

          {data?.fetchedAt && (
            <p className="privacy-meta" data-testid="privacy-meta">
              Ultimo aggiornamento:{" "}
              {new Date(data.fetchedAt * 1000).toLocaleDateString("it-IT", {
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}
              {" · "}
              <a href={data.source} target="_blank" rel="noreferrer">
                Versione originale ↗
              </a>
            </p>
          )}
        </div>
      </main>

      <footer className="privacy-footer">
        <span>© {new Date().getFullYear()} Max Valle · Siti da Incubo</span>
        <a href="https://maxvalle.it" target="_blank" rel="noreferrer">
          maxvalle.it
        </a>
      </footer>

      <style>{`
        .privacy-shell {
          min-height: 100vh;
          background: #FAF7F2;
          color: #1B1F23;
          font-family: 'Nunito', system-ui, -apple-system, sans-serif;
        }
        .privacy-header {
          max-width: 880px;
          margin: 0 auto;
          padding: 28px 24px 12px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
        }
        .privacy-brand img { height: 38px; display: block; }
        .privacy-back-link {
          font-size: 13px;
          color: #6B7280;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        .privacy-back-link:hover { color: #E56A1F; }
        .privacy-main { max-width: 880px; margin: 0 auto; padding: 16px 24px 48px; }
        .privacy-card {
          background: #fff;
          border-radius: 18px;
          padding: 40px 44px;
          box-shadow: 0 10px 40px rgba(20,30,40,0.06);
          border: 1px solid #EFE7DA;
        }
        @media (max-width: 640px) {
          .privacy-card { padding: 28px 22px; border-radius: 14px; }
        }
        .privacy-title-row {
          display: flex; align-items: center; gap: 14px; margin-bottom: 6px;
        }
        .privacy-shield { color: #E56A1F; }
        .privacy-title-row h1 {
          margin: 0;
          font-size: 28px;
          font-weight: 800;
          color: #1B1F23;
          letter-spacing: -0.01em;
        }
        .privacy-lede {
          font-size: 15px;
          color: #6B7280;
          margin: 4px 0 28px;
          font-style: italic;
        }
        .privacy-loading {
          display: flex; align-items: center; gap: 10px;
          color: #6B7280; padding: 36px 0;
        }
        .privacy-loading .spin { animation: spin 1s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .privacy-error {
          display: flex; gap: 14px;
          background: #FEF3F2; border: 1px solid #FECDCA;
          padding: 18px 20px; border-radius: 10px;
          color: #B42318;
        }
        .privacy-error strong { display: block; margin-bottom: 4px; }
        .privacy-error p { margin: 0 0 8px; color: #7A271A; font-size: 14px; }
        .privacy-fallback-link {
          color: #E56A1F; font-weight: 600; text-decoration: none;
        }
        .privacy-fallback-link:hover { text-decoration: underline; }

        /* Inherited HTML from privacylab.it: cleanup typography */
        .privacy-content { line-height: 1.6; font-size: 15px; color: #2B2F34; }
        .privacy-content h1,
        .privacy-content h2,
        .privacy-content h3,
        .privacy-content h4 {
          color: #1B1F23;
          font-weight: 700;
          margin: 28px 0 10px;
          line-height: 1.3;
        }
        .privacy-content h1 { font-size: 22px; }
        .privacy-content h2 { font-size: 19px; border-bottom: 1px solid #EFE7DA; padding-bottom: 6px; }
        .privacy-content h3 { font-size: 17px; }
        .privacy-content h4 { font-size: 15px; }
        .privacy-content p { margin: 8px 0 12px; }
        .privacy-content ul, .privacy-content ol { padding-left: 22px; margin: 8px 0 14px; }
        .privacy-content li { margin: 4px 0; }
        .privacy-content table {
          border-collapse: collapse; width: 100%; margin: 14px 0;
          font-size: 14px;
        }
        .privacy-content th, .privacy-content td {
          border: 1px solid #EFE7DA; padding: 8px 10px; text-align: left;
          vertical-align: top;
        }
        .privacy-content th { background: #FAF7F2; font-weight: 700; }
        .privacy-content a { color: #E56A1F; text-decoration: underline; }
        .privacy-content a:hover { color: #C2520F; }
        .privacy-content img { max-width: 100%; height: auto; }
        .privacy-content .company-header,
        .privacy-content .company-infos { font-size: 13px; color: #6B7280; }
        .privacy-content .doc-code { font-size: 11px; color: #9CA3AF; margin-top: 8px; }

        .privacy-meta {
          margin-top: 32px;
          padding-top: 18px;
          border-top: 1px dashed #EFE7DA;
          font-size: 13px;
          color: #6B7280;
        }
        .privacy-meta a { color: #E56A1F; }

        .privacy-footer {
          max-width: 880px; margin: 0 auto;
          padding: 24px; text-align: center;
          color: #9CA3AF; font-size: 13px;
          display: flex; justify-content: space-between; align-items: center;
        }
        .privacy-footer a { color: #6B7280; text-decoration: none; }
        .privacy-footer a:hover { color: #E56A1F; }
      `}</style>
    </div>
  );
}
